import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toCSV, downloadCSV, inDateRange } from "@/lib/csv";
import { PageHeader, Card, Btn, Field, Input, Select, Combobox, Empty } from "@/components/UI";
import { toast } from "sonner";

const emptyLine = { product_id: "", fabric_kg: "", dozens_out: "", lot_no: "" };
const empty = { date: today(), worker_id: "", notes: "", lines: [{ ...emptyLine }] };

// "dozen.pcs" hybrid parsing — 10.11 = 10 doz + 11 pcs = 131 pcs (NOT 10.11*12).
// Fractional part is the raw pcs count (0-11); >11 is invalid (12 pcs = 1 doz).
function parseDozenPcs(raw) {
  const s = String(raw || "").trim();
  if (!s) return { doz: 0, pcs: 0, total: 0, valid: false, bad: false };
  const [dozStr, pcsStr = "0"] = s.split(".");
  const doz = parseInt(dozStr, 10) || 0;
  const pcs = parseInt(pcsStr.slice(0, 2), 10) || 0;
  const bad = pcs > 11;
  return { doz, pcs, total: doz * 12 + pcs, valid: !bad && (doz > 0 || pcs > 0), bad };
}
// Format pcs back into "d.pp" hybrid string for edit prefill.
function pcsToHybridStr(pcs) {
  const n = Math.max(0, parseInt(pcs, 10) || 0);
  const doz = Math.floor(n / 12);
  const rem = n % 12;
  return rem === 0 ? String(doz) : `${doz}.${String(rem).padStart(2, "0")}`;
}

export default function CuttingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [stitchers, setStitchers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [sendModal, setSendModal] = useState(null); // { cutting_ids: [] } — single or bulk
  const [workers, setWorkers] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tab, setTab] = useState("entries");

  const load = () => api.get("/cutting").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/workers").then((r) => setWorkers(r.data.filter((w) => w.active !== false)));
    api.get("/parties").then((r) => setStitchers(r.data.filter((p) => p.type === "stitcher")));
  }, []);

  const toggleSel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const openSend = (ids) => {
    setSendModal({ cutting_ids: ids });
  };
  const submitSend = async (payload) => {
    try {
      const { data } = await api.post("/cutting/send-to-stitching", payload);
      toast.success(`Stitching DC ${data.dc_number} created`);
      setSendModal(null);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const startEdit = (c) => {
    setEditing(c.id);
    // Single-line edit path — reuse the multi-line form with just one line.
    // Convert stored pcs_out back to hybrid "d.pp" (e.g. 131 → "10.11").
    setF({
      date: c.date,
      worker_id: c.worker_id || "",
      notes: c.notes || "",
      lines: [{
        product_id: c.product_id,
        fabric_kg: String(c.fabric_kg || ""),
        dozens_out: c.pcs_out ? pcsToHybridStr(c.pcs_out) : "",
        lot_no: c.lot_no || "",
      }],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(empty); };

  const updateLine = (idx, patch) => setF((prev) => {
    const next = [...prev.lines]; next[idx] = { ...next[idx], ...patch };
    return { ...prev, lines: next };
  });
  const addLine = () => setF((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }));
  const removeLine = (idx) => setF((prev) => ({
    ...prev,
    lines: prev.lines.length === 1 ? [{ ...emptyLine }] : prev.lines.filter((_, i) => i !== idx),
  }));

  const submit = async (e) => {
    e.preventDefault();
    // Validate hybrid dozen.pcs first — fractional part must be 0-11.
    for (let idx = 0; idx < f.lines.length; idx++) {
      const parsed = parseDozenPcs(f.lines[idx].dozens_out);
      if (f.lines[idx].dozens_out && parsed.bad) {
        return toast.error(`Line ${idx + 1}: fractional part must be 0–11 pcs (12 pcs = 1 dozen)`);
      }
    }
    const clean = f.lines
      .map((l) => ({ ...l, _parsed: parseDozenPcs(l.dozens_out) }))
      .filter((l) => l.product_id && l._parsed.total > 0 && Number(l.fabric_kg) > 0);
    if (clean.length === 0) return toast.error("Add at least one line with product, kg and dozens");
    try {
      if (editing) {
        // Legacy single-line edit — take first clean line.
        const l = clean[0];
        await api.put(`/cutting/${editing}`, {
          date: f.date, worker_id: f.worker_id || null, notes: f.notes,
          product_id: l.product_id,
          fabric_kg: Number(l.fabric_kg),
          dozens_out: Number(l.dozens_out),
          pcs_out: l._parsed.total,
          lot_no: l.lot_no || "",
        });
        toast.success("Cutting updated");
      } else {
        // Multi-line: POST each line sequentially so worker/notes/date are shared.
        for (const l of clean) {
          await api.post("/cutting", {
            date: f.date, worker_id: f.worker_id || null, notes: f.notes,
            product_id: l.product_id,
            fabric_kg: Number(l.fabric_kg),
            dozens_out: Number(l.dozens_out),
            pcs_out: l._parsed.total,
            lot_no: (l.lot_no || "").toUpperCase(),
          });
        }
        toast.success(`Cutting saved · ${clean.length} line${clean.length === 1 ? "" : "s"}`);
      }
      cancelEdit(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/cutting/${id}`); load();
  };

  const filtered = useMemo(() => list.filter((c) => inDateRange(c.date, from, to)), [list, from, to]);

  const report = useMemo(() => {
    const map = {};
    filtered.forEach((c) => {
      const d = new Date(c.date);
      const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      const day = t.getUTCDay() || 7;
      t.setUTCDate(t.getUTCDate() + 4 - day);
      const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
      const week = Math.ceil((((t - yearStart) / 86400000) + 1) / 7);
      const wkKey = `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
      const worker = c.worker_name || "Unassigned";
      const key = `${worker}|${wkKey}`;
      if (!map[key]) map[key] = { worker, week: wkKey, jobs: 0, kg: 0, pcs: 0 };
      map[key].jobs += 1;
      map[key].kg += Number(c.fabric_kg) || 0;
      map[key].pcs += Number(c.pcs_out) || 0;
    });
    return Object.values(map).sort((a, b) => b.week.localeCompare(a.week) || b.pcs - a.pcs);
  }, [filtered]);

  const exportReport = () => {
    const cols = [{ key: "week", label: "Week" }, { key: "worker", label: "Worker" }, { key: "jobs", label: "Jobs" }, { key: "kg", label: "Fabric Kg" }, { key: "pcs", label: "Pcs Cut" }];
    downloadCSV(`slkc-cutting-weekly-${from || "all"}-${to || "today"}.csv`, toCSV(report, cols));
  };

  return (
    <div>
      <PageHeader label="Stage 05" title="Cutting" subtitle="Fabric (Kg) → Cut Pieces (Pcs). Track cutter, view weekly totals."
        right={
          <div className="flex items-center gap-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-2 bg-white border border-border font-mono text-xs" data-testid="cutting-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-2 bg-white border border-border font-mono text-xs" data-testid="cutting-to" />
          </div>
        }
      />
      <div className="px-4 md:px-8 pt-4 flex gap-2">
        {["entries", "report"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`cutting-tab-${t}`}
            className={`h-8 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold ${tab === t ? "bg-foreground text-background" : "bg-white border border-border"}`}>
            {t === "entries" ? "Entries" : "Weekly Report"}
          </button>
        ))}
      </div>

      {tab === "entries" && (
        <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
          <Card title={editing ? "Edit Cutting Job" : "New Cutting Job"} right={editing && (
            <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive">Cancel</button>
          )}>
            <form onSubmit={submit} className="space-y-3" data-testid="cutting-form">
              <Field label="Date"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
              <Field label="Person Cut">
                <Select data-testid="cutting-worker" value={f.worker_id} onChange={(e) => setF({ ...f, worker_id: e.target.value })}>
                  <option value="">— Unassigned —</option>
                  {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </Select>
              </Field>

              <div className="border-t border-border pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-caps">Lines · {f.lines.length}</div>
                  {!editing && (
                    <button type="button" onClick={addLine} data-testid="cutting-add-line" className="label-caps text-accent hover:underline">+ Add line</button>
                  )}
                </div>

                <div className="space-y-3">
                  {f.lines.map((l, idx) => {
                    const parsed = parseDozenPcs(l.dozens_out);
                    const linePcs = parsed.total;
                    const lineRatio = l.fabric_kg && linePcs ? (linePcs / Number(l.fabric_kg)).toFixed(2) : 0;
                    return (
                      <div key={idx} className="border border-border p-2 bg-secondary/30 space-y-2" data-testid={`cutting-line-${idx}`}>
                        <div className="flex items-center justify-between">
                          <div className="label-caps text-muted-foreground">Line {idx + 1}</div>
                          {!editing && f.lines.length > 1 && (
                            <button type="button" onClick={() => removeLine(idx)} data-testid={`cutting-remove-line-${idx}`} className="label-caps text-destructive hover:underline">Remove</button>
                          )}
                        </div>
                        <Field label="Product / SKU">
                          <Combobox
                            testid={`cutting-product-${idx}`}
                            required
                            value={l.product_id}
                            onChange={(v) => updateLine(idx, { product_id: v })}
                            placeholder="Type to search product / SKU / size…"
                            options={products.map((p) => ({
                              value: p.id,
                              label: `${p.style_name} / ${p.sku} / ${p.size} / ${p.color}`,
                              hint: p.hsn ? `HSN ${p.hsn}` : "",
                            }))}
                          />
                        </Field>
                        <div className="grid grid-cols-2 gap-2">
                          <Field label="Fabric Kg"><Input data-testid={`cutting-kg-${idx}`} type="number" step="0.01" required value={l.fabric_kg} onChange={(e) => updateLine(idx, { fabric_kg: e.target.value })} /></Field>
                          <Field label="Dozens (doz.pcs)">
                            <Input
                              data-testid={`cutting-dozens-${idx}`}
                              type="text"
                              inputMode="decimal"
                              pattern="^\d+(\.\d{1,2})?$"
                              required
                              value={l.dozens_out}
                              onChange={(e) => updateLine(idx, { dozens_out: e.target.value })}
                              placeholder="e.g. 10.11 (=131 pcs)"
                            />
                          </Field>
                        </div>
                        {l.dozens_out && (
                          <div className={`text-[11px] tabular ${parsed.bad ? "text-destructive" : "text-muted-foreground"}`} data-testid={`cutting-line-preview-${idx}`}>
                            {parsed.bad
                              ? `⚠ max .11 (12 pcs = 1 dozen)`
                              : <>= <b>{parsed.doz}</b> doz{parsed.pcs ? <> + <b>{parsed.pcs}</b> pcs</> : null} = <b>{linePcs}</b> pcs{lineRatio ? <> · <b>{lineRatio}</b> pcs/kg</> : null}</>}
                          </div>
                        )}
                        <Field label="Lot # (traceability)">
                          <Input data-testid={`cutting-lot-${idx}`} value={l.lot_no} onChange={(e) => updateLine(idx, { lot_no: e.target.value.toUpperCase() })} placeholder="LOT-30S-A12" />
                        </Field>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
              <Btn data-testid="cutting-submit">{editing ? "Save Changes" : (() => {
                const n = f.lines.filter((l) => l.product_id && parseDozenPcs(l.dozens_out).total > 0 && Number(l.fabric_kg) > 0).length;
                return `Save Cutting (${n} line${n === 1 ? "" : "s"})`;
              })()}</Btn>
            </form>
          </Card>
          <Card title={`Cutting History · ${filtered.length}`} className="lg:col-span-2">
            <div className="table-scroll">
              <table className="table-dense w-full">
                <thead><tr>
                  <th><input type="checkbox" data-testid="cutting-select-all" onChange={(e) => { if (e.target.checked) setSelected(new Set(filtered.filter((c) => (c.pcs_out - (c.sent_to_stitching_pcs||0)) > 0).map((c) => c.id))); else setSelected(new Set()); }} /></th>
                  <th>Date</th><th>Product</th><th>Lot #</th><th>Cutter</th><th className="text-right">Kg</th><th className="text-right">Doz</th><th className="text-right">Pcs</th><th className="text-right">Sent</th><th className="text-right">Bal</th><th></th>
                </tr></thead>
                <tbody>
                  {filtered.map((c) => {
                    const sent = c.sent_to_stitching_pcs || 0;
                    const bal = (c.pcs_out || 0) - sent;
                    // Display dozens in hybrid "d.pp" notation derived from pcs_out (source of truth).
                    const dozensHybrid = c.pcs_out ? pcsToHybridStr(c.pcs_out) : "0";
                    return (
                    <tr key={c.id} className={editing === c.id ? "bg-secondary" : ""}>
                      <td>{bal > 0 && <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} data-testid={`cutting-sel-${c.id}`} />}</td>
                      <td className="font-mono">{c.date}</td>
                      <td>{c.product_label}</td>
                      <td className="font-mono text-xs">{c.lot_no ? (
                        <Link to={`/lots/${encodeURIComponent(c.lot_no)}`} className="hover:underline text-accent">{c.lot_no}</Link>
                      ) : <span className="text-muted-foreground">—</span>}</td>
                      <td>{c.worker_name || <span className="text-muted-foreground">—</span>}</td>
                      <td className="tabular text-right">{c.fabric_kg}</td>
                      <td className="tabular text-right font-bold" data-testid={`cutting-row-doz-${c.id}`}>{dozensHybrid}</td>
                      <td className="tabular text-right">{c.pcs_out}</td>
                      <td className="tabular text-right text-xs">{sent}</td>
                      <td className={`tabular text-right font-bold ${bal > 0 ? "text-accent" : "text-muted-foreground"}`}>{bal}</td>
                      <td className="text-right whitespace-nowrap">
                        {bal > 0 && (
                          <button onClick={() => openSend([c.id])} className="label-caps text-accent hover:underline mr-3" data-testid={`cutting-send-${c.id}`}>→ Stitch</button>
                        )}
                        {isAdmin && (<>
                          <button onClick={() => startEdit(c)} className="label-caps hover:text-foreground mr-3">Edit</button>
                          <button onClick={() => del(c.id)} className="label-caps hover:text-destructive">Del</button>
                        </>)}
                      </td>
                    </tr>
                  );})}
                  {filtered.length === 0 && <tr><td colSpan={11}><Empty text="No cutting jobs in this range" /></td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {tab === "report" && (
        <div className="p-4 md:p-8">
          <Card title={`Weekly Cutting by Person · ${report.length} rows`} right={
            <button onClick={exportReport} data-testid="cutting-report-csv" className="h-8 px-3 font-mono text-[10px] tracking-[0.15em] uppercase font-bold bg-foreground text-background">↓ CSV</button>
          }>
            <div className="table-scroll">
              <table className="table-dense w-full">
                <thead><tr><th>Week</th><th>Worker</th><th className="text-right">Jobs</th><th className="text-right">Fabric Kg</th><th className="text-right">Pcs Cut</th></tr></thead>
                <tbody>
                  {report.map((r) => (
                    <tr key={`${r.worker}-${r.week}`}>
                      <td className="font-mono">{r.week}</td>
                      <td className="font-semibold">{r.worker}</td>
                      <td className="tabular text-right">{r.jobs}</td>
                      <td className="tabular text-right">{r.kg.toFixed(2)}</td>
                      <td className="tabular text-right font-bold">{r.pcs}</td>
                    </tr>
                  ))}
                  {report.length === 0 && <tr><td colSpan={5}><Empty text="No data — record cutting jobs with a worker to see the report" /></td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-foreground text-background px-6 py-3 flex items-center gap-4 shadow-xl z-40" data-testid="cutting-bulk-bar">
          <span className="font-mono text-sm"><b>{selected.size}</b> selected</span>
          <button onClick={() => openSend([...selected])} data-testid="cutting-bulk-send" className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-accent text-background">→ Send to Stitching</button>
          <button onClick={() => setSelected(new Set())} className="label-caps text-background/70 hover:text-background">Clear</button>
        </div>
      )}
      {sendModal && (
        <SendToStitchingModal
          jobs={list.filter((c) => sendModal.cutting_ids.includes(c.id))}
          stitchers={stitchers}
          onClose={() => setSendModal(null)}
          onSubmit={submitSend}
        />
      )}
    </div>
  );
}

function SendToStitchingModal({ jobs, stitchers, onClose, onSubmit }) {
  const [f, setF] = useState({ stitcher_id: "", date: today(), rate: "", notes: "" });
  const totalPcs = jobs.reduce((s, j) => s + Math.max(0, (j.pcs_out || 0) - (j.sent_to_stitching_pcs || 0)), 0);
  const totalDoz = pcsToHybridStr(totalPcs);
  const submit = (e) => {
    e.preventDefault();
    if (!f.stitcher_id) return toast.error("Choose a stitcher");
    onSubmit({
      stitcher_id: f.stitcher_id, date: f.date,
      cutting_ids: jobs.map((j) => j.id),
      rate: Number(f.rate || 0), notes: f.notes,
    });
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-background border-2 border-border w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="cutting-send-modal">
        <div>
          <div className="label-caps text-accent">Send to Stitching</div>
          <div className="font-display font-black text-xl mt-1">{jobs.length} cutting job{jobs.length === 1 ? "" : "s"}</div>
          <div className="text-xs font-mono text-muted-foreground mt-1">Total: <b>{totalDoz}</b> doz ({totalPcs} pcs)</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Stitcher">
            <Select required value={f.stitcher_id} onChange={(e) => setF({ ...f, stitcher_id: e.target.value })} data-testid="send-stitch-party">
              <option value="">Select stitcher…</option>
              {stitchers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="DC Date"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Rate ₹/doz"><Input type="number" step="0.01" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 label-caps border border-border hover:bg-secondary">Cancel</button>
            <Btn data-testid="send-stitch-submit">Create Stitching DC</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}
